'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { AlertTriangle, TrendingUp, Info, Building2, Clock } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const shippingRoutes = [
  { id: 'route-001', route: 'Red Sea / Suez Canal', status: 'Disrupted', risk: 'Critical', vessels: 847, delay: '+12–14 days', costImpact: '+22% freight insurance', acmeExposure: '1 active Acme Corp shipment rerouted', action: 'Monitor rerouting — notify downstream customers' },
  { id: 'route-002', route: 'Taiwan Strait', status: 'Elevated Risk', risk: 'High', vessels: 1240, delay: 'Potential closure', costImpact: 'Insurance surcharge pending', acmeExposure: 'East Asia electronics sourcing at risk', action: 'Assess alternative sourcing — monitor PLA exercises' },
  { id: 'route-003', route: 'Strait of Hormuz', status: 'Monitoring', risk: 'High', vessels: 2100, delay: 'No current delay', costImpact: 'Premium elevated +8%', acmeExposure: 'Energy commodity procurement exposure', action: 'Continue monitoring — no immediate action' },
  { id: 'route-004', route: 'Malacca Strait', status: 'Normal', risk: 'Medium', vessels: 3200, delay: 'None', costImpact: 'Standard rates', acmeExposure: 'APAC logistics corridor — low risk', action: 'No action required' },
  { id: 'route-005', route: 'Cape of Good Hope', status: 'Congested', risk: 'Medium', vessels: 420, delay: '+2–3 days', costImpact: '+5% fuel surcharge', acmeExposure: 'Red Sea rerouting overflow — secondary impact', action: 'Factor into delivery window planning' },
];

const riskColor: Record<string, string> = {
  Critical: 'text-danger bg-danger/10',
  High: 'text-warning bg-warning/10',
  Medium: 'text-info bg-info/10',
  Low: 'text-success bg-success/10',
};

const statusColor: Record<string, string> = {
  'Disrupted': 'text-danger',
  'Elevated Risk': 'text-warning',
  'Monitoring': 'text-info',
  'Normal': 'text-success',
  'Congested': 'text-warning',
};

export default function MaritimeRiskPage() {
  return (
    <AppLayout activeRoute="/maritime-risk">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Maritime & Logistics Risk</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Shipping lane disruptions, chokepoint risk, and port intelligence</p>
          </div>
          <Link href="/alerts" className="flex items-center gap-2 text-sm bg-primary text-primary-foreground font-medium rounded-md px-4 py-2 hover:opacity-90 transition-all">
            View Maritime Alerts
          </Link>
        </div>

        {/* How teams use this */}
        <div className="card-elevated rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={15} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">How teams use this</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Logistics and operations teams use maritime disruption data to reroute shipments before delays materialise, renegotiate freight contracts, update delivery window commitments to customers, and quantify insurance exposure across active shipping corridors.
              </p>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Disrupted Routes', value: '3', unit: 'corridors', icon: AlertTriangle, color: 'text-danger', sub: 'Red Sea, Taiwan Strait, Hormuz' },
            { label: 'Avg. Delay Impact', value: '+12', unit: 'days', icon: Clock, color: 'text-warning', sub: 'Red Sea rerouting via Cape' },
            { label: 'Freight Cost Increase', value: '+22%', unit: '', icon: TrendingUp, color: 'text-warning', sub: 'Insurance premium surcharge' },
            { label: 'Acme Corp Shipments', value: '1', unit: 'affected', icon: Building2, color: 'text-danger', sub: 'Active rerouting in progress' },
          ].map(({ label, value, unit, icon: Icon, color, sub }) => (
            <div key={label} className="card-elevated rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className={color} />
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <p className={`text-2xl font-bold font-tabular ${color}`}>{value}</p>
                {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
              </div>
              <p className="text-2xs text-muted-foreground">{sub}</p>
            </div>
          ))}
        </div>

        {/* Acme Corp Exposure */}
        <div className="card-elevated rounded-xl border border-danger/20 bg-danger/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 size={14} className="text-danger" />
            <p className="text-sm font-semibold text-foreground">Acme Corp — Maritime Exposure</p>
            <span className="text-2xs px-2 py-0.5 rounded bg-danger/20 text-danger font-semibold ml-auto">ACTIVE DISRUPTION</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            1 Red Sea route affected — estimated delay +12 days, freight cost +22%. MV Stellaris rerouted via Cape of Good Hope. Downstream delivery windows for Q2 consumer goods shipment require immediate customer notification. Review insurance coverage for extended transit.
          </p>
        </div>

        {/* Shipping Routes Table */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <p className="text-sm font-semibold text-foreground">Critical Shipping Corridors</p>
            <p className="text-xs text-muted-foreground mt-0.5">Real-time status of major global shipping lanes</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Route</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Risk</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Delay Impact</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Cost Impact</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Acme Exposure</th>
                </tr>
              </thead>
              <tbody>
                {shippingRoutes.map((route, idx) => (
                  <tr key={route.id} className={`border-b border-border hover:bg-muted/30 transition-colors ${idx % 2 === 1 ? 'bg-muted/10' : ''}`}>
                    <td className="px-4 py-3 font-medium text-foreground">{route.route}</td>
                    <td className={`px-4 py-3 font-medium ${statusColor[route.status]}`}>{route.status}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-2xs font-semibold ${riskColor[route.risk]}`}>{route.risk}</span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{route.delay}</td>
                    <td className="px-4 py-3 text-muted-foreground">{route.costImpact}</td>
                    <td className="px-4 py-3 text-muted-foreground">{route.acmeExposure}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
